import {
  Button,
  LabeledInput,
  LabeledSelect,
  SkubotSpinner,
  Text,
} from "@commonsku/styles";
import React, { useEffect, useState } from "react";
import { GoBackButton } from "./Questionnaire";
import { useDispatch, useSelector } from "react-redux";
import { getClientOptions } from "../../../selectors/dropdowns";
import { shopDetails } from "./constants";
import {
  createLoadClientList,
  LOAD_CLIENT_LIST_SUCCESS,
} from "../../../actions/client";
import {
  selectIsLoading,
  setIsLoadingFalse,
  setIsLoadingTrue,
} from "../../../redux/isLoading";
import { createShop } from "../../../actions/shop";
import { StyledSpinnerDiv } from "./TemplateSelection";
import { useIdentity } from "../../../hooks";
import { trackEvent } from "../../../utils/analytics";

interface CreateShopPageProps {
  shopType: string;
  handleCustomRouting: (
    newRoute: string,
    selectedShopType?: string,
    shopName?: string,
    clientId?: string
  ) => void;
  initialClientId?: string | null;
}

const defaultPublicViewTemplateId = "f406cdd4-2be1-11e8-b92b-0a67125bdbf8";

const CreateShopPage = ({
  shopType,
  handleCustomRouting,
  initialClientId,
}: CreateShopPageProps) => {
  const dispatch = useDispatch();
  const clients = useSelector(getClientOptions);
  const [isLoaded, setIsLoaded] = useState<boolean>(!!clients.length);
  const [clientId, setClientId] = useState<string | null>(initialClientId);
  const [shopName, setShopName] = useState<string>("");
  const isLoading = useSelector(selectIsLoading);
  const identity = useIdentity();

  useEffect(() => {
    if (!isLoaded) {
      dispatch(createLoadClientList() as unknown as Promise<any>).then(
        (action) => {
          if (action.type === LOAD_CLIENT_LIST_SUCCESS) {
            setIsLoaded(true);
          }
        }
      );
    }
  }, [dispatch, isLoaded]);

  const options = clients.map(
    (client: { client_name: string; client_id: string }) => ({
      label: client.client_name,
      value: client.client_id,
    })
  );
  const shopclientType =
    shopType === "COMPANY" || shopType === "POPUP" || shopType === "SHOPIFY" || shopType === "POD"
      ? "single_client"
      : "multiple_client";
  const isCreatingMarketingShopFromClientPage =
    shopType === "MARKETING" && !!initialClientId;

  const createShopifyShop = async (isCustomize = false): Promise<void> => {
    dispatch(setIsLoadingTrue());
    try {
      await dispatch(
        createShop(
          shopName,
          clientId,
          1,
          true,
          defaultPublicViewTemplateId,
          isCustomize,
          1,
          "SHOPIFY"
        )
      );
    } catch (error) {
      dispatch(setIsLoadingFalse());
      throw error;
    }
  };

  const handleContinueClick = () => {
    if (shopType === "POD") {
      trackEvent("PoD - Create", {
        tenant_id: identity.tenant_id,
        login_email: identity.login_email,
      });
    }

    if (shopType === "SHOPIFY") {
      createShopifyShop();
      return;
    }
    handleCustomRouting("templateSelection", shopType, shopName, clientId);
  };

  return (
    <div
      style={{
        margin: "auto",
        marginTop: 182,
        marginBottom: 182,
        width: "50%",
        overflow: "hidden",
      }}
    >
      <Text as="h1" style={{ fontWeight: 600, fontSize: 32, marginBottom: 64 }}>
        Create a {shopDetails[shopType].type} Shop
      </Text>
      <div style={{ maxWidth: 384, textAlign: "left", margin: "auto" }}>
        <LabeledInput
          label="Shop Name"
          style={{ marginBottom: 24 }}
          onChange={(e) => setShopName(e.target.value.trim())}
          required
        />
        {isCreatingMarketingShopFromClientPage && (
          <Text as="p" style={{ marginBottom: 24 }}>
            Creating a Marketing Shop from a client page doesn't automatically
            assign that client.
          </Text>
        )}
        {"single_client" === shopclientType && (
          <>
            <LabeledSelect
              label="Client"
              options={options}
              value={options.find((v) => v.value === clientId)}
              isSearchable
              onChange={(e) => setClientId(e.value)}
              required
              error={isLoaded && clients.length === 0}
              menuListStyles={{
                maxWidth: "382px",
                height: "150px",
                overflowY: "auto",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
              isDisabled={!isLoaded}
            />
            {isLoaded && clients.length === 0 && (
              <Text as="span" color="error">
                You need to add some clients before you can create a{" "}
                {shopDetails[shopType].type} Shop
              </Text>
            )}
          </>
        )}
      </div>
      <div style={{ marginTop: 64 }}>
        <Button
          size="huge"
          style={{ display: "block", margin: "auto", marginBottom: 16 }}
          disabled={
            !shopName || (shopclientType === "single_client" && !clientId)
          }
          onClick={handleContinueClick}
          id="shopCreation-createShopPage-continue.btn"
        >
          Continue
        </Button>
        <GoBackButton
          variant="text"
          size="small"
          style={{ display: "block", margin: "auto" }}
          onClick={() => handleCustomRouting("initialize")}
          id="shopCreation-selectedShopPage-goBack.btn"
        >
          Take Me Back to Shop Selection
        </GoBackButton>
        {isLoading && (
          <StyledSpinnerDiv>
            <SkubotSpinner skubot style={{ margin: "auto" }} size="huge" />
          </StyledSpinnerDiv>
        )}
      </div>
    </div>
  );
};

export default CreateShopPage;
